import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  apiPost,
  companyIdSelector,
  DrawerType,
  getBulkFindAreaPath,
  IApiError,
  IRydeProcess,
  Log,
  normalizeErrors,
  PLURAL_KEY,
  RydeProcessTitle,
  RydeUiUnit,
  SimpleResult,
  useDrawers,
} from "@ryde/shared";

const STATION_ENTITY: RydeUiUnit = "station";

export const useStationsBulkActions = () => {
  const { t } = useTranslation();
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setFetching = useSetRecoilState(apiDataFetchingAtom);
  const companyId = useRecoilValue(companyIdSelector);
  const { openDrawer } = useDrawers();

  const bulkFindArea = useEvent(async (ids: number[]): Promise<boolean> => {
    if (!companyId) {
      Log.warn("bulkFindArea aborted: companyId is not loaded yet");
      return false;
    }

    if (!ids.length) {
      return false;
    }

    try {
      setErrors(undefined);
      setFetching(true);
      const requestUrl = getBulkFindAreaPath(companyId, STATION_ENTITY);
      const result = await apiPost<SimpleResult<IRydeProcess>>(requestUrl, {
        body: { ids },
      });

      if (result?.success) {
        const resultData = result?.data as IRydeProcess;
        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkFindArea,
            entity: STATION_ENTITY,
          },
        });
        return true;
      }

      return false;
    } catch (error) {
      Log.errorAndToast(
        t("errors.bulkFailToFindAreaEntities", {
          entity: t(`${STATION_ENTITY}.${PLURAL_KEY}`),
        }),
        error,
      );
      setErrors(normalizeErrors(error as IApiError, t));
      return false;
    } finally {
      setFetching(false);
    }
  });

  return {
    bulkFindArea,
  };
};
